import type {
  FreightCostsQuery,
  ScenarioTimeline_CoaFragment,
} from '../../../graphql';
import type { LoadEvent, TimeData } from '../../core/TimeData';
import { EventCategory } from '../../core/Core';
import { getOrderCoaDetails } from '../getOderCoaDetails';
import { compact } from 'lodash';

export type FreightCostsCoa = NonNullable<
  NonNullable<FreightCostsQuery['allCoaContracts']>['nodes'][number]
>;

export type FreightCostsOrder = NonNullable<
  NonNullable<FreightCostsQuery['allOrders']>['nodes'][number]
>;

/** The Voyage P&L query and the planning page spread the same Baf_Coa fragment. */
export type PricedCoa = FreightCostsCoa | ScenarioTimeline_CoaFragment;

export function prepareCoaInput(coa: PricedCoa) {
  const freights = compact(coa.coaContractPricesByCoaContractId.nodes);
  const coaAssortments = compact(
    coa.coaContractAssortmentsByCoaContractId.nodes,
  ).map((a) => ({
    ...a,
    assortmentId: a.assortmentId ?? null,
    assortmentGroupId: a.assortmentGroupId ?? null,
  }));
  const routes = compact(coa.coaContractPortsByCoaContractId.nodes).map(
    (route) => ({
      ...route,
      originId: route.originId ?? null,
      originZoneId: route.originZoneId ?? null,
      destinationId: route.destinationId ?? null,
      destinationZoneId: route.destinationZoneId ?? null,
      destinationZoneLocations: new Set(
        compact(route.zoneByDestinationZoneId?.zoneLocationsByZoneId.nodes).map(
          (zl) => zl.locationId,
        ),
      ),
      originZoneLocations: new Set(
        compact(route.zoneByOriginZoneId?.zoneLocationsByZoneId.nodes).map(
          (zl) => zl.locationId,
        ),
      ),
    }),
  );

  return {
    cargoUnitName: coa.cargoUnitByAmountUnitId?.name ?? null,
    freights,
    coaAssortments,
    routes,
  };
}

/**
 * Pre-compute the highest COA freight rate per COA for COAs with useHighestFreightRate enabled.
 * Resolves each order's freight rate via assortment+route matching, then picks the max per COA.
 */
export function getMaxCoaFreightRateByCoaId(
  loadEvents: TimeData<LoadEvent>[],
  orderById: Map<number, FreightCostsOrder>,
  coaContracts: FreightCostsQuery['allCoaContracts'],
): Map<number, number> {
  const result = new Map<number, number>();

  for (const loadEvent of loadEvents) {
    if (loadEvent.event.category !== EventCategory.COA) continue;
    const { entry } = loadEvent.event;
    const order = orderById.get(loadEvent.event.order.id);
    if (!order || !('coaId' in entry)) continue;

    const coa = coaContracts?.nodes.find((c) => c?.id === entry.coaId);
    if (!coa?.useHighestFreightRate) continue;

    const { coaFreight } = getOrderCoaDetails(
      {
        assortmentId: order.assortmentId ?? null,
        assortmentGroupId:
          order.assortmentByAssortmentId?.assortmentGroup ?? null,
        loadPortId: order.loadPortId ?? null,
        dischargePortId: order.dischargePortId,
      },
      { mass: loadEvent.mass, volume: loadEvent.volume },
      prepareCoaInput(coa),
      order.orderVoyageCharterRelationsByOrderId?.nodes.at(0)?.coaRouteId,
    );

    if (coaFreight?.price != null) {
      const rate = Number(coaFreight.price);
      const prev = result.get(coa.id);
      if (prev === undefined || rate > prev) {
        result.set(coa.id, rate);
      }
    }
  }

  return result;
}
